import { FC, useState } from 'react';
import DataTable from '@/components/functional/DataTable/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import useChildUsers, { ChildUser } from '@/api/useChildUsers';
import { useNavigate } from 'react-router-dom';
import useRemoveChildUser from '@/api/useRemoveChildUser';
import useCreateChildUser from '@/api/useCreateChildUser';
import useReinviteChildUser from '@/api/useReinviteChildUser';
import useUser from '@/api/useUser';
import Modal from '@/components/modals/Modal/Modal';
import { Button } from 'react-daisyui';
import { toast } from 'react-toastify';
import { TrashIcon } from '@heroicons/react/24/outline';


// Need to define handlers outside of columns
let handleShowReportFunc: (userId: string) => void;
let handleRemoveFunc: (email: string) => void;
let handleReinviteFunc: (email: string) => void;

const columns: ColumnDef<ChildUser>[] = [
  {
    accessorKey: 'name',
    header: 'Organization Name',
    cell: ({ row }) => (
      <span className="text-gray-900 font-semibold">{row.original.name}</span>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span className={`inline-flex text-sm font-medium ${
        row.original.status === 'Active' 
          ? 'text-success' 
          : 'text-[#FFCC00]'
      }`}>
        {row.original.status}
      </span>
    ),
  },
  {
    id: 'report',
    header: 'Report',
    cell: ({ row }) => 
      row.original.status === 'Active' ? (
        <button 
          onClick={() => handleShowReportFunc(row.original.id)}
          className="text-black rounded-3xl px-3 py-1 bg-[#F2F2F2] font-medium"
        >
          Show Reports
        </button>
      ) : null,
  },
  {
    id: 'actions',
    header: 'Action',
    cell: ({ row }) => (
      <div className="flex items-center space-x-3">
        {row.original.status === 'Pending' && (
          <button
            onClick={() => handleReinviteFunc(row.original.email || '')}
            className="flex items-center gap-1.5 font-medium py-1 rounded transition-colors"
            title="Resend invitation"
          >
            <span className="relative before:content-[''] before:absolute before:left-0 before:right-0 before:bottom-[3px] before:h-[1px] before:bg-black">
              Reinvite
            </span>
          </button>
        )}
      </div>
    ),
  },
  {
    id: 'remove',
    header: () => <div className="w-full"></div>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <button
          onClick={() => handleRemoveFunc(row.original.name)}
          className="text-red-500 hover:text-red-700 transition-colors p-1 rounded hover:bg-red-50"
          title="Remove organization"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    ),
  },
  
];

const OrganizationsPage: FC = () => {
  const navigate = useNavigate();
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<{ email: string; name: string } | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);

  const { data: user } = useUser();
  const { data: childUsers = [], isLoading, refetch } = useChildUsers();
  const removeChildUser = useRemoveChildUser();
  const createChildUser = useCreateChildUser();
  const reinviteChildUser = useReinviteChildUser();

  const handleRemoveClick = (email: string, name: string) => {
    setUserToRemove({ email, name });
    setIsRemoveModalOpen(true);
    setRemoveError(null);
  };

  const handleConfirmRemove = async () => {
    if (!userToRemove) return;

    try {
      setRemoveError(null);
      await removeChildUser.mutateAsync({ email: userToRemove.email });
      toast.success(`Organization "${userToRemove.name}" has been successfully removed.`);
      setIsRemoveModalOpen(false);
      setUserToRemove(null);
      void refetch();
    } catch (error) {
      console.error('Failed to remove user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to remove organization. Please try again.';
      setRemoveError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleCancelRemove = () => {
    setIsRemoveModalOpen(false);
    setUserToRemove(null);
    setRemoveError(null);
  };

  const handleShowReport = (userId: string) => {
    // Find the user to get their data
    const user = childUsers.find(u => u.id === userId);
    // Navigate to child reports page to show all completed assessments
    if (user && user.authId) {
      navigate(`/child-reports/${userId}?name=${encodeURIComponent(user.name)}&email=${encodeURIComponent(user.email || '')}&authId=${user.authId}`);
    } else {
      navigate(`/child-reports/${userId}`);
    }
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
    setInviteEmail('');
    setInviteError(null);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteEmail('');
    setInviteError(null);
  };

  const handleConfirmInvite = async () => {
    if (!inviteEmail) {
      setInviteError('Please enter an email address');
      return;
    }

    try {
      setInviteError(null);
      // Use email as name for now - backend can handle this
      await createChildUser.mutateAsync({ email: inviteEmail, name: inviteEmail });
      toast.success(`Invitation sent to ${inviteEmail}`);
      handleCloseInviteModal();
      void refetch();
    } catch (error) {
      console.error('Failed to create child user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send invitation. Please try again.';
      setInviteError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleReinvite = async (email: string) => {
    try {
      await reinviteChildUser.mutateAsync({ email });
      toast.success(`Invitation resent to ${email}`);
    } catch (error) {
      console.error('Failed to reinvite user:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend invitation. Please try again.';
      toast.error(errorMessage);
    }
  };

  // Calculate if user can invite more children
  const invitationLimit = user?.invitation_limit ?? 0;
  const canInvite = childUsers.length < invitationLimit;

  // Make handlers available to columns
  handleShowReportFunc = handleShowReport;
  handleRemoveFunc = (email: string) => {
    const user = childUsers.find(u => u.email === email);
    if (user) {
      handleRemoveClick(email, user.name);
    }
  };
  handleReinviteFunc = handleReinvite;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Child Structure</h1>
        <Button 
          onClick={handleOpenInviteModal}
          disabled={!canInvite}
          className="bg-black text-white "
        >
          + Invite
        </Button>
      </div>

      {/* Invitation limit info */}
      {invitationLimit > 0 && (
        <div className="text-sm text-gray-600">
          {childUsers.length} / {invitationLimit} organizations used
          {!canInvite && (
            <span className="ml-2 text-orange-600 font-medium">
              (Limit reached)
            </span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      ) : childUsers.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-gray-400 mb-2">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No child organizations yet</h3>
          <p className="text-sm text-gray-500 mb-4">
            Get started by inviting your first child organization using the "+ Invite" button above.
          </p>
        </div>
      ) : (
        <DataTable
          data={childUsers}
          columns={columns}
          className="w-full [&_tbody_tr:nth-child(odd)]:bg-table-stripe overflow-hidden"

        />
      )}

      {/* Invite Modal */}
      <Modal.Wrapper 
        isOpen={isInviteModalOpen} 
        onClose={handleCloseInviteModal}
        closeable={!createChildUser.isPending}
      >
        <div className="flex flex-col gap-4">
          
          <div className="flex flex-col gap-4">
            <div>
              <label className="block text-sm  text-gray-[#1F2937] mb-3">
                Email
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                disabled={createChildUser.isPending}
                autoFocus
              />
            </div>

          
          </div>

          {inviteError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{inviteError}</span>
            </div>
          )}
        </div>

        <Modal.Nav>
          <Button 
            color="primary" 
            onClick={handleConfirmInvite}
            disabled={createChildUser.isPending || !inviteEmail}
            className="w-full bg-black text-white hover:bg-gray-800"
          >
            {createChildUser.isPending ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Sending...
              </span>
            ) : (
              'Send an invitation'
            )}
          </Button>
        </Modal.Nav>
      </Modal.Wrapper>

      {/* Remove Confirmation Modal */}
      <Modal.Wrapper 
        isOpen={isRemoveModalOpen} 
        onClose={handleCancelRemove}
        closeable={!removeChildUser.isPending}
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-xl font-semibold text-gray-900">Remove Child Organization</h3>
          
          {userToRemove && (
            <div className="text-gray-700">
              <p>Are you sure you want to remove the following organization?</p>
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="font-semibold">{userToRemove.name}</p>
                <p className="text-sm text-gray-600">{userToRemove.email}</p>
              </div>
              <p className="mt-3 text-sm text-red-600">
                <strong>Warning:</strong> This action cannot be undone. The organization will lose access to the system.
              </p>
            </div>
          )}

          {removeError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{removeError}</span>
            </div>
          )}
        </div>

        <Modal.Nav>
          <Button 
            color="ghost" 
            onClick={handleCancelRemove}
            disabled={removeChildUser.isPending}
          >
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={handleConfirmRemove}
            disabled={removeChildUser.isPending}
            className="ml-auto"
          >
            {removeChildUser.isPending ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Removing...
              </span>
            ) : (
              'Remove Organization'
            )}
          </Button>
        </Modal.Nav>
      </Modal.Wrapper>
    </div>
  );
};

export default OrganizationsPage;
